import { Container, Rectangle, type Renderer, RenderTexture, Sprite, Texture, TilingSprite } from "pixi.js";

import { OCEAN } from "../../../shared/Config";
import { packColor, tintBetween } from "../../../shared/Settings";
import { WAVE_HEIGHT, WaveTrain } from "./Waves";
import { WIND_SPEED } from "../../../shared/Wind";
import { OceanFilter } from "./OceanFilter";

/** Direction the wave layer drifts. Normalised on use. */
const WAVES_DIRECTION = { x: -1, y: -1 };
/** Scale of the wave tile pattern; below 1 makes the surface texture finer. */
const WAVES_TILE_SCALE = 0.5;

/**
 * Whether the scrolling overlay is drawn at all, and how fast it crawls in tile-space pixels
 * per second. **Tunable**, on the Ocean panel — see design/Settings.md.
 *
 * Read every frame rather than snapshotted into the sprite, so the dashboard's workspace can
 * switch the layer off under a running plate. The drift is pre-scaled into a velocity so the
 * speed means the same on any heading.
 */
let OVERLAY_ON = OCEAN.overlay !== 0;
let OVERLAY_VELOCITY = scaleToSpeed(WAVES_DIRECTION, OCEAN.overlaySpeed);

/**
 * Puts new numbers behind the two dials above.
 *
 * **For the dashboard's workspace and nothing else**, exactly as `setWaveDials` in Waves.ts is —
 * see the note there. The overlay is the surface's texture and nothing reads it but the eye, so
 * two clients drawing it differently is two people looking at different water.
 */
export function setOverlayDials(enabled: boolean, speed: number): void {
    OVERLAY_ON = enabled;
    OVERLAY_VELOCITY = scaleToSpeed(WAVES_DIRECTION, speed);
}

function scaleToSpeed(direction: { x: number; y: number }, speed: number): { x: number; y: number } {
    const length = Math.hypot(direction.x, direction.y) || 1;
    return { x: (direction.x / length) * speed, y: (direction.y / length) * speed };
}

/**
 * How white the tops of the waves go, at the strongest wind the dashboard offers.
 *
 * Scaled by `windSpeed` on the way in, so turning the wind up makes the sea visibly wilder without
 * a dial of its own — a hard blow breaks the crests and a light air does not.
 *
 * **It is measured against the foam threshold**, which is the thing to know before moving it.
 * A crest's white goes into the same channel the wake's foam does and comes out through the
 * same cut, so a cap that lands under `FOAM_THRESHOLD` is not a faint whitecap — it is no
 * whitecap at all. This was 0.72, which after the wind factor and `waveHeight` reached 0.162
 * against a threshold of 0.18, and the sea has never broken a crest at the shipped dials.
 * Raised until a full crest in a moderate wind clears it with a body to spare.
 *
 * What still holds it back from one: whitecaps compete directly with the loot for the eye,
 * and the loot has to win (design/Water.md).
 */
const CAP_STRENGTH = 0.95;
/**
 * The wind at which the crests are breaking as hard as they ever will, in px/s. Well short of the
 * top of `windSpeed`'s range: the shipped 60 has to already show some white, or the term only
 * exists for a dial nobody has moved — which is exactly what it did at 160.
 */
const CAP_FULL_WIND = 90;
/**
 * Flat colour behind the wave overlay. Stands in for real base art. **Tunable**, on the Ocean
 * panel — see design/Settings.md.
 *
 * Exported because anything drawn *inside* the sea has to be tinted against it to read as
 * submerged — [SeaLife.ts](SeaLife.ts) washes its animals towards this. One definition, so
 * changing the sea's colour cannot leave the things in it behind. It is a `let` for the same
 * reason the swell's dials are, and moved by the same one caller: see `setWaterColors`.
 */
export let SEA_COLOR = packColor(OCEAN.seaColor);
/**
 * Extra sea drawn beyond the viewport on every side. Both warps in the filter — the swell's slope
 * and the wake's push — pull pixels in from off the plate, so without this the screen edges sample
 * nothing and fringe. Comfortably more than either reaches; it costs a border of water nobody
 * sees, which is cheaper than measuring it exactly and being wrong when a dial moves.
 */
const OVERSCAN = 120;

/**
 * What the trail texture is cleared to before the marks are drawn into it: **still water with no
 * foam on it**.
 *
 * The two halves of that read differently, which is the whole reason this is not black.
 * [OceanFilter.ts](OceanFilter.ts) takes red and green as `channel - 0.5`, the direction the water is
 * pushed, so those two have to start at mid-grey or the entire sea is shoved up and to the left.
 * Blue is a quantity — how much foam — so it starts at nothing.
 */
const TRAIL_NEUTRAL = 0x808000;
/**
 * Colour of the foam the filter paints. A very pale blue rather than white: on the shipped
 * `#0090f4` pure white reads as a hole in the water rather than as churn in it. **Tunable**,
 * beside the base colour.
 */
let FOAM_COLOR = packColor(OCEAN.foamColor);

/**
 * The sea's palette, changed under a running plate.
 *
 * **For the dashboard's workspace and nothing else**, exactly as `setWaveDials` in Waves.ts is —
 * see the note there. Colours are as far from the simulation as a setting gets: nothing anywhere
 * reads the sea's colour to decide anything, and two clients drawing different water is two
 * people looking at different pictures of one fight.
 *
 * The two module-level colours are set here; the plate and the filter that read them are
 * instance state, so `Water.setColors` is what actually pushes them at a sea on screen.
 */
export function setWaterColors(sea: string, foam: string): void {
    SEA_COLOR = packColor(sea);
    FOAM_COLOR = packColor(foam);
}

/** How white a crest goes in a given wind. A light air barely marks the water; a hard blow tears it. */
function capStrengthFor(windSpeed: number): number {
    return CAP_STRENGTH * Math.min(1, windSpeed / CAP_FULL_WIND);
}

/**
 * The signature visual: a sea plate built from a base water sprite and a wave overlay, drawn
 * through one filter that computes the swell, lights it, breaks its tops, and lays the wake on it.
 *
 * **The sea is world-anchored.** It used to be pure screen furniture — a texture with no world
 * size to be wrong about, scrolled by however far the camera had moved. It stopped being allowed
 * to be that the moment a crest became a thing a hull can be positioned on: the water a player
 * sees has to be the water the arena publishes, at the coordinates it publishes it at. So the
 * plate is handed the camera and the zoom, and the filter works out where in the sea each pixel
 * is. See design/Water.md.
 *
 * The plate itself stays in **screen pixels** and is oversized by OVERSCAN so the warps always
 * have water to pull from. Everything that rides on the swell (wakes, splashes, foam) belongs
 * inside `container` so it goes through the same filter.
 */
export class Water {
    private readonly _container = new Container();
    private readonly _base: Sprite;
    private readonly _underwater = new Container();
    private readonly _waveOverlay: TilingSprite;
    private readonly _renderer: Renderer;
    /**
     * The trail, as an image. Rebuilt every frame from `_trailLayer` and read by the filter; see
     * `setTrail` for what is in it and `_drawTrail` for why it is rebuilt rather than carried.
     */
    private readonly _trailTexture: RenderTexture;
    private readonly _trailSprite: Sprite;
    private readonly _sea: OceanFilter;
    /**
     * What is drawn into that texture. Empty until a wake claims it, which is not a special case
     * — an empty layer renders as the neutral clear, which is a sea with nothing on it.
     */
    private _trailLayer: Container = new Container();

    private _width = 0;
    private _height = 0;
    private _plateWidth = 0;
    private _plateHeight = 0;
    /** Where the viewport is in the sea, and how far back the camera is pulled. See `setCamera`. */
    private _cameraX = 0;
    private _cameraY = 0;
    private _zoom = 1;

    public get container(): Container {
        return this._container;
    }

    /**
     * Where things that are **below the surface** go: above the flat base, under the wave
     * overlay, and inside the filter, so the overlay passes over them and the swell warps
     * them along with the water they are in.
     *
     * Its origin is the **top-left of the viewport**, not the centre of the plate, so a
     * child can be positioned in plain screen coordinates and ignore the overscan entirely.
     * Nothing loose in the sea should have to know how the plate is built.
     *
     * Note that `filterArea` clips to the plate: a child more than OVERSCAN beyond the
     * viewport edge is cut off. That is off screen, so it does not matter — but it is why
     * nothing here should be relied on to look right while it is out of shot.
     */
    public get underwater(): Container {
        return this._underwater;
    }

    constructor(width: number, height: number, renderer: Renderer) {
        this._renderer = renderer;

        // A stretched white pixel, tinted. Flat colour, so the displacement warp does not
        // show in this layer at all — the swell now reads entirely through the overlay.
        this._base = new Sprite({
            texture: Texture.WHITE,
            anchor: 0.5,
            tint: SEA_COLOR
        });
        this._container.addChild(this._base);
        // Between the base and the overlay: whatever is down here is *in* the water, so the
        // waves have to run over the top of it.
        this._container.addChild(this._underwater);

        this._waveOverlay = new TilingSprite({
            texture: Texture.from("wave_overlay"),
            anchor: 0.5,
            tileScale: { x: WAVES_TILE_SCALE, y: WAVES_TILE_SCALE }
        });
        this._container.addChild(this._waveOverlay);

        // Sampled through a UV and never drawn, at one device pixel per CSS pixel whatever the
        // screen is: what is in it is big and soft, and a retina copy would be four times the
        // memory for detail neither the warp nor the foam threshold can show.
        this._trailTexture = RenderTexture.create({
            width: Math.max(1, width),
            height: Math.max(1, height),
            resolution: 1,
            dynamic: true
        });
        // It covers the viewport and not the plate, so the overscan samples past its edge. What
        // it finds there is the neutral border, which is the right answer: water nobody can see
        // is not being pushed by anything and has no foam on it.
        this._trailTexture.source.addressMode = "clamp-to-edge";

        this._trailSprite = new Sprite({ texture: this._trailTexture, anchor: 0.5 });
        this._container.addChild(this._trailSprite);

        // One pass for the swell, the light on it, the wake's warp and the foam together — see
        // OceanFilter.ts for why they cannot sensibly be separate. The trail sprite is in the
        // scene only so that the filter has a world transform to read; it makes it unrenderable
        // itself, and the same transform is how a pixel finds itself in the sea.
        this._sea = new OceanFilter({
            trail: this._trailSprite,
            height: WAVE_HEIGHT,
            // A light air barely marks the water and a hard blow tears the tops off it.
            capStrength: capStrengthFor(WIND_SPEED),
            // The picked colours as the multipliers the shader wants — see OceanFilter.ts on why
            // the depth ramp multiplies, and `tintBetween` for the one guarded division.
            deepTint: tintBetween(OCEAN.seaColor, OCEAN.troughColor),
            crestTint: tintBetween(OCEAN.seaColor, OCEAN.crestColor),
            // Nothing until a wake says otherwise, so a sea with no trail in it is a plain swell.
            trailScale: 0,
            foamColor: FOAM_COLOR,
            foamStrength: 0
        });
        this._container.filters = [this._sea];

        this.resize(width, height);
    }

    public update(deltaSeconds: number): void {
        this._drawTrail();

        // Switched here rather than when the dial moves, because the dial is module-level and
        // every plate that exists has to follow it. Hiding the layer leaves the flat base and
        // the filter's own shading: the swell's warp has nothing left to bend, which is what
        // makes this a legible way to see what the overlay is worth. See design/Water.md.
        this._waveOverlay.visible = OVERLAY_ON;

        // A drift of its own, on top of where the camera has put it: the overlay is surface
        // texture rather than the swell, and water moves whether or not anybody is sailing.
        this._waveOverlay.tilePosition.x += OVERLAY_VELOCITY.x * deltaSeconds;
        this._waveOverlay.tilePosition.y += OVERLAY_VELOCITY.y * deltaSeconds;
    }

    /**
     * Puts the sea where the camera is: the world position the viewport is centred on, and how far
     * back the hull has pulled it (design/Ships.md).
     *
     * What still wants an absolute camera is the **overlay**: its texture is anchored to the
     * world, so its detail travels with the water rather than sliding over it. The waves
     * themselves no longer ask — they are measured from the camera and carry its travel in their
     * own phase (`Waves.ts`), which is what takes the arena's seam out of the sea entirely.
     */
    public setCamera(worldX: number, worldY: number, zoom: number): void {
        this._cameraX = worldX;
        this._cameraY = worldY;
        this._zoom = zoom;

        this._sea.setFrame(this._width, this._height, zoom);

        // tilePosition is tile space, and tileScale shrinks it on the way to the screen, so the
        // world offset is divided back out for the texture to sit still against the water.
        this._waveOverlay.tileScale.set(WAVES_TILE_SCALE * zoom);
        this._waveOverlay.tilePosition.set(
            -(worldX * zoom) / (WAVES_TILE_SCALE * zoom),
            -(worldY * zoom) / (WAVES_TILE_SCALE * zoom)
        );
    }

    /**
     * How tall the sea reads and how hard it is blowing over it, after either has been changed
     * under a running plate.
     *
     * Only the dashboard's workspace does that — see `setWaveDials` in Waves.ts for why the game
     * itself never does. The constructor takes the same two numbers off the config and this is the
     * same arithmetic, so a sea that has been re-dialled is a sea that was built that way.
     */
    public setSwell(height: number, windSpeed: number): void {
        this._sea.setSwell(height, capStrengthFor(windSpeed));
    }

    /**
     * The sea's colours, as the four the Ocean panel stores: the flat base the whole plate is
     * built on, what the water is tinted toward in a trough and on a crest, and the one white
     * both the wake's foam and a breaking crest are painted in.
     *
     * Only the dashboard's workspace calls it — see `setWaterColors` above, which it calls first
     * so that anything reading `SEA_COLOR` on its own (the school) follows too.
     */
    public setColors(sea: string, trough: string, crest: string, foam: string): void {
        setWaterColors(sea, foam);

        this._base.tint = SEA_COLOR;
        this._sea.setPalette(tintBetween(sea, trough), tintBetween(sea, crest), FOAM_COLOR);
    }

    /** The sea to draw, straight off the scene's own `Waves`. An empty one is a flat calm. */
    public setWaves(sea: readonly WaveTrain[]): void {
        this._sea.setWaves(sea);
    }

    public resize(width: number, height: number): void {
        const plateWidth = width + OVERSCAN * 2;
        const plateHeight = height + OVERSCAN * 2;

        this._width = width;
        this._height = height;
        this._plateWidth = plateWidth;
        this._plateHeight = plateHeight;

        this._base.setSize(plateWidth, plateHeight);
        this._waveOverlay.setSize(plateWidth, plateHeight);

        this._container.position.set(width / 2, height / 2);
        // The plate is centred on the viewport, so backing out half of it puts the underwater
        // layer's origin on the viewport's top-left corner.
        this._underwater.position.set(-width / 2, -height / 2);

        // Resized in place rather than rebuilt: the filter holds this texture's source, and a
        // fresh texture would leave it reading the old one.
        this._trailTexture.resize(Math.max(1, width), Math.max(1, height));
        this._trailSprite.setSize(width, height);
        // Known bounds, so the filter system can skip measuring the plate every frame.
        // filterArea is in the container's local space — the plate is centred on it.
        this._container.filterArea = new Rectangle(-plateWidth / 2, -plateHeight / 2, plateWidth, plateHeight);

        // The frame is measured in the viewport, so a resize moves it even when the camera has not.
        this._sea.setFrame(width, height, this._zoom);
    }

    public destroy(): void {
        this._container.destroy({ children: true });

        // After the container, and by hand. `destroy({ children: true })` takes the sprite that
        // reads this texture but never the texture itself, so a render target would be left on
        // the GPU every time a scene changed — and taking it first would leave that sprite being
        // torn down around a source that had already gone.
        this._trailTexture.destroy(true);
    }

    /**
     * Hands the sea the wake that marks it: a layer to draw the trail map from, how hard that
     * trail bends the water, and how solid its foam reads.
     *
     * `field` is **not** part of the scene. Every frame it is rendered on its own into the trail
     * texture, and the whole sea is then drawn through that — red and green in it push the water,
     * blue is foam. That is what `WakeField.field` holds; see design/Water.md.
     *
     * Optional. A `Water` nobody calls this on draws a plain swell, because an empty layer renders
     * as the neutral clear and the filter's two trail terms are already zero.
     */
    public setTrail(field: Container, displacement: number, foam: number): void {
        this._trailLayer = field;
        this._sea.trailScale = displacement;
        this._sea.foamStrength = foam;
    }

    /**
     * Redraws the trail map from the layer that feeds it.
     *
     * Cleared to neutral and rebuilt from scratch every frame rather than scrolled and faded.
     * The marks already hold their own positions and their own ages, so redrawing them is exact;
     * carrying the previous frame's texture forward would mean shifting it by however far the
     * camera moved, which blurs a little more with every sub-pixel step and has nothing sensible
     * to say at the arena's seam. See design/Water.md.
     */
    private _drawTrail(): void {
        this._renderer.render({
            container: this._trailLayer,
            target: this._trailTexture,
            clear: true,
            clearColor: TRAIL_NEUTRAL
        });
    }
}
